import { useState, useRef } from "react";
import {
  FileText,
  ListChecks,
  Circle,
  ChevronRight,
  ChevronDown,
  MoreHorizontal,
  Plus,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState } from "@/components/feedback/EmptyState";
import {
  useAgentFiles,
  useDuplicateAgentFile,
  useCreateAgentFile,
  useReorderAgentFiles,
  useMoveAgentFile,
} from "@/hooks/useAgentFiles";
import type { AgentFileNode } from "@/types/agent-skills";
import { NewAgentDialog } from "@/components/scratchpad/NewAgentDialog";
import { RenameDialog } from "@/components/scratchpad/RenameDialog";
import { DeleteConfirmDialog } from "@/components/scratchpad/DeleteConfirmDialog";
import { ChangelogPanel } from "@/components/scratchpad/ChangelogPanel";

// ── Depth helpers ─────────────────────────────────────────────────────────────

function depthOf(kind: AgentFileNode["kind"]): number {
  if (kind === "agent") return 0;
  if (kind === "skill") return 1;
  return 2; // subskill
}

function childKindFor(parentKind: AgentFileNode["kind"]) {
  if (parentKind === "agent") return "skill" as const;
  if (parentKind === "skill") return "subskill" as const;
  return null; // subskills cannot have children
}

/** Given a dragged node kind and a potential parent kind, is the drop valid? */
function isValidDrop(
  draggedKind: AgentFileNode["kind"],
  targetParentKind: AgentFileNode["kind"] | null
): boolean {
  if (draggedKind === "agent") {
    // agents can only live at root (null parent)
    return targetParentKind === null;
  }
  if (draggedKind === "skill") {
    return targetParentKind === "agent";
  }
  if (draggedKind === "subskill") {
    return targetParentKind === "skill";
  }
  return false;
}

// ── Node icon ─────────────────────────────────────────────────────────────────

function NodeIcon({ kind }: { kind: AgentFileNode["kind"] }) {
  const cls = "w-3.5 h-3.5 text-muted-foreground flex-shrink-0";
  if (kind === "agent") return <FileText className={cls} />;
  if (kind === "skill") return <ListChecks className={cls} />;
  return <Circle className={cls} />;
}

// ── Kind badge ────────────────────────────────────────────────────────────────

function KindBadge({ kind }: { kind: AgentFileNode["kind"] }) {
  if (kind === "agent") {
    return (
      <span className="text-[9px] font-semibold uppercase tracking-wide px-1 py-0.5 rounded bg-indigo-500/20 text-indigo-300 flex-shrink-0 leading-none">
        agent
      </span>
    );
  }
  if (kind === "skill") {
    return (
      <span className="text-[9px] font-semibold uppercase tracking-wide px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-400 flex-shrink-0 leading-none">
        skill
      </span>
    );
  }
  return (
    <span className="text-[9px] font-semibold uppercase tracking-wide px-1 py-0.5 rounded bg-amber-500/20 text-amber-400 flex-shrink-0 leading-none">
      sub
    </span>
  );
}

// ── Inline-create input row ───────────────────────────────────────────────────

const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

interface InlineCreateRowProps {
  depth: number;
  onConfirm: (name: string) => void;
  onCancel: () => void;
}

function InlineCreateRow({ depth, onConfirm, onCancel }: InlineCreateRowProps) {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const indentClass = depth === 0 ? "ml-0" : depth === 1 ? "ml-4" : "ml-8";
  const isValid = SLUG_RE.test(value);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (isValid) onConfirm(value);
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  return (
    <div
      className={cn(
        indentClass,
        "h-8 rounded-md text-sm flex items-center gap-1 px-2",
        "bg-white/[0.04] border border-indigo-500/30"
      )}
    >
      <input
        ref={inputRef}
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Slight delay so Enter doesn't blur before the handler runs.
          setTimeout(onCancel, 150);
        }}
        placeholder="slug-name"
        className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/50 font-mono min-w-0"
        aria-label="New item name"
      />
      {value && !isValid && (
        <span className="text-[10px] text-destructive flex-shrink-0">lowercase + hyphens</span>
      )}
    </div>
  );
}

// ── Single tree row (sortable) ────────────────────────────────────────────────

interface TreeRowProps {
  node: AgentFileNode;
  depth: number;
  isSelected: boolean;
  onSelect: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onAddChild: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
  /** dnd-kit drag listeners forwarded from useSortable (applied to the row) */
  dragListeners?: React.HTMLAttributes<HTMLDivElement>;
  /** dnd-kit drag attributes forwarded from useSortable (role/tabIndex etc; role is excluded to preserve treeitem) */
  dragAttributes?: Omit<React.HTMLAttributes<HTMLDivElement>, "role">;
  isDragging?: boolean;
  isDropTarget?: boolean;
}

function TreeRow({
  node,
  depth,
  isSelected,
  onSelect,
  onRename,
  onDuplicate,
  onDelete,
  onAddChild,
  expanded,
  onToggleExpand,
  dragListeners,
  dragAttributes,
  isDragging,
  isDropTarget,
}: TreeRowProps) {
  const indentClass = depth === 0 ? "ml-0" : depth === 1 ? "ml-4" : "ml-8";
  const canAddChild = childKindFor(node.kind) !== null;
  // Chevron is always visible for agent/skill (Change 4)
  const hasExpandChevron = node.kind !== "subskill";

  return (
    <div
      className={cn(
        indentClass,
        "h-8 rounded-md text-sm flex items-center gap-1 px-2",
        "hover:bg-white/[0.06] group cursor-pointer",
        isSelected && "bg-indigo-500/15 text-indigo-300",
        isDragging && "opacity-50",
        isDropTarget && "ring-1 ring-indigo-400/60 bg-indigo-500/10"
      )}
      role="treeitem"
      aria-selected={isSelected}
      aria-expanded={hasExpandChevron ? expanded : undefined}
      onClick={onSelect}
      {...dragListeners}
      {...dragAttributes}
    >
      {/* Expand/collapse chevron — always visible for agent+skill (Change 4) */}
      {hasExpandChevron ? (
        <button
          className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded hover:bg-white/[0.1]"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
          aria-label={expanded ? "Collapse" : "Expand"}
          tabIndex={-1}
        >
          {expanded ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </button>
      ) : (
        <span className="flex-shrink-0 w-4 h-4" />
      )}

      <NodeIcon kind={node.kind} />

      <span className="flex-1 truncate min-w-0">{node.name}</span>

      <KindBadge kind={node.kind} />

      {/* Inline "+ Add skill" / "+ Add subskill" affordance — visible on hover */}
      {canAddChild && (
        <button
          className="flex-shrink-0 flex items-center gap-0.5 text-[10px] text-muted-foreground opacity-0 group-hover:opacity-70 hover:!opacity-100 hover:text-foreground px-1 py-0.5 rounded hover:bg-white/[0.08] transition-opacity"
          aria-label={node.kind === "agent" ? "Add skill" : "Add subskill"}
          tabIndex={-1}
          onClick={(e) => {
            e.stopPropagation();
            onAddChild();
          }}
        >
          <Plus className="w-2.5 h-2.5" />
          {node.kind === "agent" ? "skill" : "sub"}
        </button>
      )}

      {/* Row actions — visible on hover (Rename, Duplicate only; Delete moved to editor) */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-5 w-5 flex-shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100",
              isSelected && "opacity-100"
            )}
            onClick={(e) => e.stopPropagation()}
            aria-label="More actions"
          >
            <MoreHorizontal className="w-3.5 h-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {canAddChild && (
            <>
              <DropdownMenuItem onSelect={onAddChild}>+ New child</DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onSelect={onRename}>Rename</DropdownMenuItem>
          <DropdownMenuItem onSelect={onDuplicate}>Duplicate</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onDelete} className="text-destructive focus:text-destructive">
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ── Droppable container (for cross-parent drop) ───────────────────────────────

interface DroppableContainerProps {
  id: string;
  children: React.ReactNode;
}

function DroppableContainer({ id, children }: DroppableContainerProps) {
  const { setNodeRef } = useDroppable({ id });
  return <div ref={setNodeRef}>{children}</div>;
}

// ── Sortable wrapper for a single node ───────────────────────────────────────

interface SortableTreeNodeProps {
  node: AgentFileNode;
  depth: number;
  selectedFileId: string | null;
  expandedIds: Set<string>;
  inlineCreateParentId: string | null;
  onSelect: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onRenameRequest: (node: AgentFileNode) => void;
  onDuplicateRequest: (id: string) => void;
  onDeleteRequest: (node: AgentFileNode) => void;
  onAddChildRequest: (parentNode: AgentFileNode) => void;
  onChildrenReorder: (parentId: string | null, orderedIds: string[]) => void;
  onInlineCreateConfirm: (parentId: string, name: string) => void;
  onInlineCreateCancel: () => void;
  overParentId: string | null;
}

function SortableTreeNode({
  node,
  depth,
  selectedFileId,
  expandedIds,
  inlineCreateParentId,
  onSelect,
  onToggleExpand,
  onRenameRequest,
  onDuplicateRequest,
  onDeleteRequest,
  onAddChildRequest,
  onChildrenReorder,
  onInlineCreateConfirm,
  onInlineCreateCancel,
  overParentId,
}: SortableTreeNodeProps) {
  const isSelected = node.id === selectedFileId;
  const expanded = expandedIds.has(node.id);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
    data: { kind: node.kind, parentId: node.parent_id },
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isDropTarget = overParentId === node.id;

  // Show children if expanded, OR if an inline-create is pending for this node
  const showChildren =
    expanded || (inlineCreateParentId === node.id && childKindFor(node.kind) !== null);

  return (
    <div ref={setNodeRef} style={style}>
      <TreeRow
        node={node}
        depth={depth}
        isSelected={isSelected}
        onSelect={() => onSelect(node.id)}
        onRename={() => onRenameRequest(node)}
        onDuplicate={() => onDuplicateRequest(node.id)}
        onDelete={() => onDeleteRequest(node)}
        onAddChild={() => onAddChildRequest(node)}
        expanded={expanded}
        onToggleExpand={() => onToggleExpand(node.id)}
        dragListeners={listeners as React.HTMLAttributes<HTMLDivElement>}
        dragAttributes={
          (({ role: _role, ...rest }) => rest)(
            attributes as React.HTMLAttributes<HTMLDivElement>
          ) as Omit<React.HTMLAttributes<HTMLDivElement>, "role">
        }
        isDragging={isDragging}
        isDropTarget={isDropTarget}
      />

      {showChildren && (
        <DroppableContainer id={`parent:${node.id}`}>
          <SortableChildList
            nodes={node.children}
            parentId={node.id}
            depth={depth + 1}
            selectedFileId={selectedFileId}
            expandedIds={expandedIds}
            inlineCreateParentId={inlineCreateParentId}
            onSelect={onSelect}
            onToggleExpand={onToggleExpand}
            onRenameRequest={onRenameRequest}
            onDuplicateRequest={onDuplicateRequest}
            onDeleteRequest={onDeleteRequest}
            onAddChildRequest={onAddChildRequest}
            onChildrenReorder={onChildrenReorder}
            onInlineCreateConfirm={onInlineCreateConfirm}
            onInlineCreateCancel={onInlineCreateCancel}
            overParentId={overParentId}
          />
          {/* Empty hint for expanded containers with no children */}
          {node.children.length === 0 && inlineCreateParentId !== node.id && (
            <div
              className={cn(
                depth === 0 ? "ml-4" : "ml-8",
                "h-5 flex items-center text-[10px] text-muted-foreground/40 px-2 select-none"
              )}
            >
              {node.kind === "agent" ? "no skills yet" : "no sub-skills yet"}
            </div>
          )}
        </DroppableContainer>
      )}

      {/* Inline-create input row for this node */}
      {inlineCreateParentId === node.id && (
        <InlineCreateRow
          depth={depth + 1}
          onConfirm={(name) => onInlineCreateConfirm(node.id, name)}
          onCancel={onInlineCreateCancel}
        />
      )}
    </div>
  );
}

// ── Sortable sibling list ─────────────────────────────────────────────────────

interface SortableChildListProps {
  nodes: AgentFileNode[];
  parentId: string | null;
  depth: number;
  selectedFileId: string | null;
  expandedIds: Set<string>;
  inlineCreateParentId: string | null;
  onSelect: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onRenameRequest: (node: AgentFileNode) => void;
  onDuplicateRequest: (id: string) => void;
  onDeleteRequest: (node: AgentFileNode) => void;
  onAddChildRequest: (parentNode: AgentFileNode) => void;
  onChildrenReorder: (parentId: string | null, orderedIds: string[]) => void;
  onInlineCreateConfirm: (parentId: string, name: string) => void;
  onInlineCreateCancel: () => void;
  overParentId: string | null;
}

function SortableChildList({
  nodes,
  parentId,
  depth,
  selectedFileId,
  expandedIds,
  inlineCreateParentId,
  onSelect,
  onToggleExpand,
  onRenameRequest,
  onDuplicateRequest,
  onDeleteRequest,
  onAddChildRequest,
  onChildrenReorder,
  onInlineCreateConfirm,
  onInlineCreateCancel,
  overParentId,
}: SortableChildListProps) {
  const ids = nodes.map((n) => n.id);

  return (
    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
      <div>
        {nodes.map((child) => (
          <SortableTreeNode
            key={child.id}
            node={child}
            depth={depth}
            selectedFileId={selectedFileId}
            expandedIds={expandedIds}
            inlineCreateParentId={inlineCreateParentId}
            onSelect={onSelect}
            onToggleExpand={onToggleExpand}
            onRenameRequest={onRenameRequest}
            onDuplicateRequest={onDuplicateRequest}
            onDeleteRequest={onDeleteRequest}
            onAddChildRequest={onAddChildRequest}
            onChildrenReorder={onChildrenReorder}
            onInlineCreateConfirm={onInlineCreateConfirm}
            onInlineCreateCancel={onInlineCreateCancel}
            overParentId={overParentId}
          />
        ))}
      </div>
    </SortableContext>
  );
}

// ── AgentFileTree (public component) ─────────────────────────────────────────

export interface AgentFileTreeProps {
  selectedFileId: string | null;
  onSelect: (id: string | null) => void;
  isDirty: boolean;
}

export function AgentFileTree({ selectedFileId, onSelect, isDirty }: AgentFileTreeProps) {
  const { tree, rows, isLoading } = useAgentFiles();
  const duplicateFile = useDuplicateAgentFile();
  const createFile = useCreateAgentFile();
  const reorderFiles = useReorderAgentFiles();
  const moveFile = useMoveAgentFile();

  // Expand/collapse state — agents are expanded by default.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Dialog state
  const [newAgentDialogOpen, setNewAgentDialogOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<AgentFileNode | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AgentFileNode | null>(null);

  // Inline-create state: which parent node currently has an open inline input
  const [inlineCreateParentId, setInlineCreateParentId] = useState<string | null>(null);

  // Cross-parent drag: track which parent container the drag is currently over
  const [overParentId, setOverParentId] = useState<string | null>(null);

  // Unsaved-changes guard state
  const [pendingSelectId, setPendingSelectId] = useState<string | null>(null);
  const [dirtyGuardOpen, setDirtyGuardOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelect = (id: string) => {
    if (id === selectedFileId) return;
    if (isDirty) {
      setPendingSelectId(id);
      setDirtyGuardOpen(true);
    } else {
      onSelect(id);
    }
  };

  const confirmDirtySwitch = () => {
    if (pendingSelectId !== null) {
      onSelect(pendingSelectId);
    }
    setPendingSelectId(null);
    setDirtyGuardOpen(false);
  };

  const cancelDirtySwitch = () => {
    setPendingSelectId(null);
    setDirtyGuardOpen(false);
  };

  const handleAddChild = (parentNode: AgentFileNode) => {
    const childKind = childKindFor(parentNode.kind);
    if (!childKind) return;
    // Auto-expand parent so the inline input appears under it.
    setExpandedIds((prev) => new Set([...prev, parentNode.id]));
    setInlineCreateParentId(parentNode.id);
  };

  const handleInlineCreateConfirm = async (parentId: string, name: string) => {
    const parentNode = rows.find((r) => r.id === parentId);
    if (!parentNode) return;
    const childKind = childKindFor(parentNode.kind as AgentFileNode["kind"]);
    if (!childKind) return;

    setInlineCreateParentId(null);
    await createFile.mutateAsync({
      parentId,
      kind: childKind,
      name,
    });
    // Keep parent expanded so the new child is visible.
    setExpandedIds((prev) => new Set([...prev, parentId]));
  };

  const handleInlineCreateCancel = () => {
    setInlineCreateParentId(null);
  };

  /**
   * Persist the reorder via RPC. The TanStack Query onMutate handler in
   * useReorderAgentFiles sets the cache to the new order synchronously,
   * so the rendered list updates immediately with no flash.
   */
  const handleChildrenReorder = (parentId: string | null, orderedIds: string[]) => {
    reorderFiles.mutate({ parentId, orderedIds });
  };

  // ── dnd-kit drag handlers ──────────────────────────────────────────────────

  function handleDragOver(event: DragOverEvent) {
    const { over } = event;
    if (!over) {
      setOverParentId(null);
      return;
    }
    // If over a parent: container id is "parent:<nodeId>"
    const overId = String(over.id);
    if (overId.startsWith("parent:")) {
      setOverParentId(overId.slice(7));
    } else {
      setOverParentId(null);
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    setOverParentId(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    const draggedRow = rows.find((r) => r.id === activeId);
    if (!draggedRow) return;

    // Determine if this is a cross-parent drop (over a "parent:<nodeId>" droppable)
    if (overId.startsWith("parent:")) {
      const newParentId = overId.slice(7);
      const newParentRow = rows.find((r) => r.id === newParentId);
      if (!newParentRow) return;

      // Validate kind rules
      if (
        !isValidDrop(
          draggedRow.kind as AgentFileNode["kind"],
          newParentRow.kind as AgentFileNode["kind"]
        )
      )
        return;
      if (draggedRow.parent_id === newParentId) return; // already in this parent

      const newSiblings = rows.filter(
        (r) => r.parent_id === newParentId && r.id !== activeId && !r.deleted_at
      );
      const position = newSiblings.length; // append at end

      moveFile.mutate({
        id: activeId,
        newParentId,
        position,
        oldParentId: draggedRow.parent_id,
      });
      return;
    }

    // Same-parent reorder: find the sibling list that contains both active and over
    if (activeId === overId) return;

    const overRow = rows.find((r) => r.id === overId);
    if (!overRow) return;

    // Must be same parent for reorder
    if (draggedRow.parent_id !== overRow.parent_id) {
      // Cross-parent drop onto a sibling row — move to that sibling's parent
      const newParentId = overRow.parent_id;
      const newParentRow = newParentId ? rows.find((r) => r.id === newParentId) : null;
      const newParentKind = newParentRow ? (newParentRow.kind as AgentFileNode["kind"]) : null;

      if (!isValidDrop(draggedRow.kind as AgentFileNode["kind"], newParentKind)) return;

      const newSiblings = rows
        .filter((r) => r.parent_id === newParentId && r.id !== activeId && !r.deleted_at)
        .sort((a, b) => a.position - b.position);
      const overSiblingIndex = newSiblings.findIndex((r) => r.id === overId);
      const position = overSiblingIndex === -1 ? newSiblings.length : overSiblingIndex;

      moveFile.mutate({
        id: activeId,
        newParentId: newParentId ?? null,
        position,
        oldParentId: draggedRow.parent_id,
      });
      return;
    }

    // Same-parent reorder
    const siblings = rows
      .filter((r) => r.parent_id === draggedRow.parent_id && !r.deleted_at)
      .sort((a, b) => a.position - b.position);

    const ids = siblings.map((n) => n.id);
    const oldIndex = ids.indexOf(activeId);
    const newIndex = ids.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(ids, oldIndex, newIndex);
    handleChildrenReorder(draggedRow.parent_id, reordered);
  }

  // The tree from useAgentFiles already reflects the optimistic cache update
  // set by useReorderAgentFiles#onMutate, so no local ordering overlay is needed.
  const displayTree = tree;

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="flex flex-col h-full p-4">
        <div className="flex-1 space-y-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-8 rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (displayTree.length === 0) {
    return (
      <div className="flex flex-col h-full p-4">
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={FileText}
            title="No agents yet"
            description="Create one to start."
            action={
              <Button size="sm" onClick={() => setNewAgentDialogOpen(true)}>
                + New Agent
              </Button>
            }
          />
        </div>

        <NewAgentDialog open={newAgentDialogOpen} onOpenChange={setNewAgentDialogOpen} />

        <ChangelogPanel />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-4">
      {/* Tree — single DndContext wraps the whole tree for cross-parent drag */}
      <div className="flex-1 overflow-y-auto space-y-0.5 min-h-0" role="tree">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <DroppableContainer id="parent:root">
            <SortableChildList
              nodes={displayTree}
              parentId={null}
              depth={0}
              selectedFileId={selectedFileId}
              expandedIds={expandedIds}
              inlineCreateParentId={inlineCreateParentId}
              onSelect={handleSelect}
              onToggleExpand={toggleExpand}
              onRenameRequest={(n) => setRenameTarget(n)}
              onDuplicateRequest={(id) => duplicateFile.mutate(id)}
              onDeleteRequest={(n) => setDeleteTarget(n)}
              onAddChildRequest={handleAddChild}
              onChildrenReorder={handleChildrenReorder}
              onInlineCreateConfirm={handleInlineCreateConfirm}
              onInlineCreateCancel={handleInlineCreateCancel}
              overParentId={overParentId}
            />
          </DroppableContainer>
        </DndContext>
      </div>

      {/* Footer: + New Agent */}
      <div className="pt-3 border-t border-border mt-3">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setNewAgentDialogOpen(true)}
        >
          + New Agent
        </Button>
      </div>

      {/* Changelog — collapsible section below the tree footer */}
      <ChangelogPanel />

      {/* Dialogs */}
      <NewAgentDialog open={newAgentDialogOpen} onOpenChange={setNewAgentDialogOpen} />

      {renameTarget && (
        <RenameDialog
          open={renameTarget !== null}
          onOpenChange={(open) => {
            if (!open) setRenameTarget(null);
          }}
          fileId={renameTarget.id}
          currentName={renameTarget.name}
        />
      )}

      <DeleteConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        node={deleteTarget}
        onDeleted={() => {
          if (deleteTarget?.id === selectedFileId) onSelect(null);
        }}
      />

      {/* Unsaved-changes guard */}
      <AlertDialog open={dirtyGuardOpen} onOpenChange={setDirtyGuardOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              Switching files will discard your current edits. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDirtySwitch}>Stay here</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDirtySwitch}
            >
              Discard and switch
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Keep depthOf exported for any callers
export { depthOf };
